import React from 'react';
import { Box, Typography } from '@mui/material';

// Import Sample Data Badge
import { SampleDataBadge } from '../../common';

/**
 * Student vs Admin Bar Chart for MBF Dashboard
 * Custom stacked bar visual with value labels
 * 
 * @param {number} studentCount - Number of students
 * @param {number} adminCount - Number of admins
 */
const MBFStudentAdminChart = ({ studentCount = 2346, adminCount = 146, isSampleData = false }) => {
    // Calculate bar heights as percentage (max bar height is 170px)
    const maxValue = Math.max(studentCount, adminCount);
    const maxBarHeight = 140; // Max filled portion height

    const studentBarHeight = (studentCount / maxValue) * maxBarHeight;
    const adminBarHeight = (adminCount / maxValue) * maxBarHeight;

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '24px',
                border: '1px solid #e6e6e6',
                padding: '12px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'flex-start',
                height: '100%',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
            }}
        >
            {/* Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: 'stretch' }}>
                <Typography
                    sx={{
                        color: '#4d4d4d',
                        fontFamily: '"Roboto", sans-serif',
                        fontSize: '16px',
                        lineHeight: '20px',
                        fontWeight: 600,
                    }}
                >
                    No. of Student V/S No. of Admin
                </Typography>
                {isSampleData && <SampleDataBadge absolute={false} />}
            </Box>

            {/* Bars Container */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '11px',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    flex: 1,
                    width: '100%',
                }}
            >
                {/* Student Bar */}
                <Box
                    sx={{
                        width: '70px',
                        height: '170px',
                        position: 'relative',
                    }}
                >
                    {/* Background Track */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '70px',
                            height: '170px',
                            bgcolor: '#fee5d0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Value Label at Top */}
                        <Typography
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: '9px',
                                color: '#4d4d4d',
                                fontFamily: '"Roboto", sans-serif',
                                fontSize: '14px',
                                lineHeight: '20px',
                                fontWeight: 500,
                            }}
                        >
                            {studentCount.toLocaleString()}
                        </Typography>
                    </Box>

                    {/* Filled Bar */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '70px',
                            height: `${studentBarHeight}px`,
                            bgcolor: '#ffb777',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'center',
                            pb: 1,
                        }}
                    >
                        {/* Label inside bar */}
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '12px',
                                lineHeight: '16px',
                                fontWeight: 500,
                            }}
                        >
                            Student
                        </Typography>
                    </Box>
                </Box>

                {/* Admin Bar */}
                <Box
                    sx={{
                        width: '70px',
                        height: '170px',
                        position: 'relative',
                    }}
                >
                    {/* Background Track */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '70px',
                            height: '170px',
                            bgcolor: '#cce5ff',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Value Label at Top */}
                        <Typography
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: '9px',
                                color: '#4d4d4d',
                                fontFamily: '"Roboto", sans-serif',
                                fontSize: '14px',
                                lineHeight: '20px',
                                fontWeight: 500,
                            }}
                        >
                            {adminCount.toLocaleString()}
                        </Typography>
                    </Box>

                    {/* Filled Bar */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '70px',
                            height: `${adminBarHeight}px`,
                            bgcolor: '#58a9ff',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {/* Label inside bar */}
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '12px',
                                lineHeight: '16px',
                                fontWeight: 500,
                            }}
                        >
                            Admin
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFStudentAdminChart;
